import React, { useState, useEffect } from 'react';
import { FiFileText, FiDownload, FiUpload, FiCheckCircle, FiClock, FiActivity, FiAlertCircle, FiAward, FiMessageSquare, FiCheck, FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';

export default function StudentExams() {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadExams();
    }, []);

    const loadExams = async () => {
        try {
            const data = await fetchAPI('/student/get_exams.php');
            setExams(data);
        } catch (err) {
            toast.error("Failed to load exams list");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedFiles.length === 0) return toast.error("Please select at least one file");

        setIsSubmitting(true);
        const processingToast = toast.loading("Processing submission...");

        try {
            let fileToUpload = null;
            let fileName = "";

            // 1. Determine if we need to convert to PDF
            const containsImages = selectedFiles.some(f => f.type.startsWith('image/'));
            const isSinglePDF = selectedFiles.length === 1 && selectedFiles[0].type === 'application/pdf';

            if (containsImages) {
                toast.loading("Compressing and Converting...", { id: processingToast });
                const pdf = new jsPDF();

                for (let i = 0; i < selectedFiles.length; i++) {
                    const file = selectedFiles[i];
                    if (i > 0) pdf.addPage();

                    // Compress if it's an image, else use as-is 
                    const imgData = file.type.startsWith('image/')
                        ? await compressImage(file)
                        : await readFileAsDataURL(file);

                    const imgProps = pdf.getImageProperties(imgData);
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
                }

                const pdfBlob = pdf.output('blob');
                fileToUpload = new File([pdfBlob], `submission_${selectedExam.id}.pdf`, { type: 'application/pdf' });
                fileName = fileToUpload.name;
            } else if (isSinglePDF) {
                fileToUpload = selectedFiles[0];
                fileName = fileToUpload.name;
            } else {
                throw new Error("Invalid file combination. Please upload images or a single PDF.");
            }

            // 2. Size Validation (15MB limit)
            const fileSizeMB = fileToUpload.size / (1024 * 1024);
            if (fileSizeMB > 15) {
                throw new Error(`File too large (${fileSizeMB.toFixed(2)}MB). Maximum allowed is 15MB.`);
            }

            // 3. Upload File
            const formData = new FormData();
            formData.append('exam_files[]', fileToUpload);

            const uploadResult = await fetchAPI('/student/upload_exam.php', {
                method: 'POST',
                body: formData
            });

            // 4. Submit metadata
            await fetchAPI('/student/submit_exam.php', {
                method: 'POST',
                body: JSON.stringify({
                    exam_id: selectedExam.id,
                    submission_url: uploadResult.url
                })
            });

            toast.success("Exam submitted successfully!", { id: processingToast });
            setIsUploadModalOpen(false);
            setSelectedFiles([]);
            loadExams();
        } catch (err) {
            toast.error(err.message, { id: processingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const readFileAsDataURL = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    const maxDim = 1600;
                    if (width > height) {
                        if (width > maxDim) {
                            height *= maxDim / width;
                            width = maxDim;
                        }
                    } else {
                        if (height > maxDim) {
                            width *= maxDim / height;
                            height = maxDim;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    resolve(canvas.toDataURL('image/jpeg', 0.7));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const isExamOpen = (endDate) => {
        if (!endDate) return true;
        return new Date(endDate) > new Date();
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 font-display">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Syncing Examination Server...</p>
        </div>
    );

    return (
        <div className="p-2 md:p-2 space-y-2 md:space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-1000 font-display">
            {/* Header */}
            <div className="bg-white p-5 md:p-12 rounded-xl shadow-xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 md:p-12 opacity-5 pointer-events-none">
                    <FiAward size={100} className="text-blue-600 md:size-40" />
                </div>
                <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[8px] md:text-[10px] font-black uppercase tracking-widest mb-3 md:mb-4">
                        <FiActivity size={10} className="animate-pulse" />
                        Academic Assessment Portal
                    </div>
                    <h1 className="text-2xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-3 md:mb-4">
                        My <span className="text-blue-600">Examinations</span>
                    </h1>
                    <p className="max-w-xl text-gray-500 font-bold text-[10px] md:text-sm tracking-tight leading-relaxed">
                        Access your question papers, submit your completed work, and review marks and feedback from your instructors.
                    </p>
                </div>
            </div>

            {/* Exams View */}
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden font-display">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Assessment Detail</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Deadline</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Grading</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions Protocol</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {exams.map((exam) => {
                                const isOpen = isExamOpen(exam.end_date);
                                const isSubmitted = !!exam.submitted_at;

                                return (
                                    <tr key={exam.id} className="group hover:bg-gray-50/80 transition-all duration-300">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg ${isSubmitted ? 'bg-green-500 shadow-green-100' : 'bg-blue-600 shadow-blue-100'}`}>
                                                    {isSubmitted ? <FiCheckCircle size={20} /> : <FiFileText size={20} />}
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-0.5">{exam.class_name}</p>
                                                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight">{exam.name}</h3>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className="inline-flex flex-col items-center">
                                                <span className="text-xs font-bold text-gray-700">{exam.end_date || 'N/A'}</span>
                                                <span className="text-[8px] font-black text-gray-400 uppercase tracking-tighter">Server Time Lock</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className={`inline-flex px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${isSubmitted ? 'bg-green-50 text-green-600' : isOpen ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                                                {isSubmitted ? 'Submitted' : isOpen ? 'Active' : 'Closed'}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            {isSubmitted ? (
                                                <div className="inline-flex flex-col items-center">
                                                    <span className="text-sm font-black text-blue-600 italic">
                                                        {exam.marks !== null ? `${exam.marks}%` : 'Pending'}
                                                    </span>
                                                    {exam.feedback && (
                                                        <div className="flex items-center gap-1 text-[8px] font-black text-gray-400 uppercase cursor-help group/tip relative">
                                                            <FiMessageSquare size={10} /> Feedback
                                                            <div className="absolute bottom-full mb-2 hidden group-hover/tip:block bg-gray-900 text-white p-3 rounded-xl text-[10px] w-48 shadow-2xl z-50 normal-case italic font-medium leading-relaxed">
                                                                "{exam.feedback}"
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-[10px] font-black text-gray-300 uppercase italic">Awaiting</span>
                                            )}
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                {exam.paper_url && (
                                                    <a
                                                        href={exam.paper_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Download Paper"
                                                        className="p-3 bg-white text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white transition-all border border-gray-100 shadow-sm"
                                                    >
                                                        <FiDownload size={14} />
                                                    </a>
                                                )}

                                                {isOpen && !isSubmitted && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedExam(exam);
                                                            setIsUploadModalOpen(true);
                                                        }}
                                                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-black uppercase tracking-widest text-[9px] shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all"
                                                    >
                                                        <FiUpload size={14} /> Submit
                                                    </button>
                                                )}

                                                {isSubmitted && exam.answer_key_url && (
                                                    <a
                                                        href={exam.answer_key_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Download Answer Key"
                                                        className="p-3 bg-orange-500 text-white rounded-lg shadow-lg shadow-orange-100 hover:bg-orange-600 transition-all border border-orange-400"
                                                    >
                                                        <FiCheckCircle size={14} />
                                                    </a>
                                                )}

                                                {isSubmitted && exam.review_url && (
                                                    <a
                                                        href={exam.review_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Download Review PDF"
                                                        className="p-3 bg-blue-600 text-white rounded-lg shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all border border-blue-500"
                                                    >
                                                        <FiFileText size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                    {exams.map((exam) => {
                        const isOpen = isExamOpen(exam.end_date);
                        const isSubmitted = !!exam.submitted_at;

                        return (
                            <div key={exam.id} className="p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-md ${isSubmitted ? 'bg-green-500 shadow-green-50' : 'bg-blue-600 shadow-blue-50'}`}>
                                            {isSubmitted ? <FiCheckCircle size={16} /> : <FiFileText size={16} />}
                                        </div>
                                        <div>
                                            <p className="text-[7px] font-black text-blue-600 uppercase tracking-widest mb-0.5">{exam.class_name}</p>
                                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-tight leading-tight">{exam.name}</h3>
                                        </div>
                                    </div>
                                    <div className={`px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-wider ${isSubmitted ? 'bg-green-50 text-green-600' : isOpen ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                                        {isSubmitted ? 'Submitted' : isOpen ? 'Active' : 'Closed'}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 py-2 border-t border-b border-gray-50 border-dashed">
                                    <div className="flex flex-col">
                                        <span className="text-[7px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">Deadline</span>
                                        <span className="text-[9px] font-bold text-gray-700">{exam.end_date || 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col text-right">
                                        <span className="text-[7px] font-black text-gray-400 uppercase tracking-tighter mb-0.5">Marks</span>
                                        <span className={`text-[10px] font-black italic ${exam.marks !== null ? 'text-blue-600' : 'text-gray-300'}`}>
                                            {isSubmitted ? (exam.marks !== null ? `${exam.marks}%` : 'Pending') : 'N/A'}
                                        </span>
                                    </div>
                                </div>

                                {isSubmitted && exam.feedback && (
                                    <div className="p-2 bg-gray-50 rounded-lg">
                                        <span className="flex items-center gap-1.5 text-[7px] font-black text-gray-400 uppercase mb-1">
                                            <FiMessageSquare size={8} /> Instructor Feedback
                                        </span>
                                        <p className="text-[9px] font-medium text-gray-600 italic">"{exam.feedback}"</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2 pt-1">
                                    {exam.paper_url && (
                                        <a
                                            href={exam.paper_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center justify-center gap-1.5 py-2 bg-gray-50 text-gray-900 rounded-lg text-[8px] font-black uppercase tracking-widest border border-gray-100"
                                        >
                                            <FiDownload size={10} /> Question Paper
                                        </a>
                                    )}

                                    {isOpen && !isSubmitted && (
                                        <button
                                            onClick={() => {
                                                setSelectedExam(exam);
                                                setIsUploadModalOpen(true);
                                            }}
                                            className="col-span-2 flex items-center justify-center gap-1.5 py-2.5 bg-blue-600 text-white rounded-lg text-[8px] font-black uppercase tracking-widest shadow-lg shadow-blue-100"
                                        >
                                            <FiUpload size={10} /> Submit Final Work
                                        </button>
                                    )}

                                    {isSubmitted && exam.answer_key_url && (
                                        <a
                                            href={exam.answer_key_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center justify-center gap-2 py-2.5 bg-orange-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-lg shadow-orange-100"
                                        >
                                            <FiCheckCircle /> Answer Memo
                                        </a>
                                    )}

                                    {isSubmitted && exam.review_url && (
                                        <a
                                            href={exam.review_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-lg shadow-blue-100"
                                        >
                                            <FiFileText /> Review PDF
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {exams.length === 0 && (
                    <div className="py-24 text-center">
                        <FiActivity size={48} className="mx-auto text-gray-100 mb-4" />
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">No synchronization detected with assessment modules</p>
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            <AnimatePresence>
                {isUploadModalOpen && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => setIsUploadModalOpen(false)}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-md"
                        />
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden p-6 md:p-12 text-center"
                        >
                            <button
                                onClick={() => setIsUploadModalOpen(false)}
                                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors hidden md:block"
                            >
                                <FiX size={20} />
                            </button>

                            <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                                <FiUpload size={28} />
                            </div>
                            <h2 className="text-xl md:text-2xl font-black text-gray-900 uppercase italic tracking-tighter mb-2">
                                Submission <span className="text-blue-600">Portal</span>
                            </h2>
                            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 md:mb-8">
                                Exam: {selectedExam?.name}
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                                <div className="space-y-2 text-left">
                                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Work Upload (Images or PDF)</label>
                                    <div className="relative group">
                                        <input
                                            required
                                            type="file"
                                            multiple
                                            accept=".pdf,image/*"
                                            className="hidden"
                                            id="examFileUpload"
                                            onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                                        />
                                        <label
                                            htmlFor="examFileUpload"
                                            className="w-full flex flex-col items-center justify-center gap-3 px-4 py-8 md:px-6 md:py-10 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-blue-50/50 hover:border-blue-200 transition-all font-display"
                                        >
                                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                                                <FiUpload size={18} />
                                            </div>
                                            <div className="text-center px-2">
                                                <p className="text-[10px] md:text-[11px] font-black text-gray-900 uppercase tracking-tight break-all">
                                                    {selectedFiles.length > 0
                                                        ? `${selectedFiles.length} item(s) • Total: ${(selectedFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)}MB`
                                                        : 'Choose File(s) or Take Photos'}
                                                </p>
                                                <p className={`text-[7px] md:text-[8px] font-bold uppercase tracking-widest mt-1 ${selectedFiles.reduce((acc, f) => acc + f.size, 0) > 15 * 1024 * 1024 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>
                                                    {selectedFiles.reduce((acc, f) => acc + f.size, 0) > 15 * 1024 * 1024
                                                        ? '⚠️ Exceeds 15MB Limit'
                                                        : 'Max 15MB total • PDF, JPG, PNG'}
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full py-3.5 md:py-4 bg-gray-900 text-white rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] shadow-xl hover:bg-blue-600 transition-all disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Processing Upload...' : 'Confirm Submission'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsUploadModalOpen(false)}
                                        className="w-full py-3 md:py-4 bg-gray-100 text-gray-500 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] hover:bg-gray-200 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
